<div class="tc-page">
  <div class="tc-save-bar">
    <div class="tc-save-bar__inner">
      <div class="tc-save-bar__left">
        <button
          type="button"
          class="tc-save-bar__back"
          data-testid="tc-back-arrow"
          [attr.aria-label]="backAriaLabelKey | translate"
          (click)="onCancel()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div class="tc-save-bar__heading">
          <div class="tc-save-bar__eyebrow-row">
            <span class="tc-save-bar__eyebrow">
              @if (isEdit) {
                {{ 'testCases.form.entityLabel' | translate }}
                @if (testCase()?.key; as key) {
                  <code
                    class="tc-save-bar__key"
                    data-testid="key-pill"
                    [attr.aria-label]="'testCases.form.keyAriaLabel' | translate: { key }"
                  >
                    {{ key }}
                  </code>
                }
              } @else {
                {{ 'testCases.form.newLabel' | translate }}
              }
            </span>
            @if (sourceBadgeKey() || sourceIsNewRequirement) {
              <span class="tc-source-badge" data-testid="source-badge">
                <span class="tc-source-badge__label">{{ 'testCases.form.source.label' | translate }}</span>
                <code class="tc-source-badge__value">
                  {{ sourceBadgeKey() ?? ('testCases.form.source.newRequirement' | translate) }}
                </code>
              </span>
            }
          </div>
          <span class="tc-save-bar__title">
            {{ (isEdit ? 'testCases.form.editTitle' : 'testCases.form.createTitle') | translate }}
          </span>
        </div>
        @if (isDirtyView()) {
          <span class="tc-dirty-pill" data-testid="tc-dirty-pill" role="status">
            <span class="tc-dirty-pill__dot" aria-hidden="true"></span>
            {{ 'testCases.form.unsavedChangesBadge' | translate }}
          </span>
        }
      </div>
      <div class="tc-save-bar__right">
        @if (!isReadOnly() && !loadError()) {
          <span
            class="tc-save-bar__run"
            data-testid="tf-ausfuehren-huelle"
            [matTooltip]="isEdit ? '' : ('testCases.form.runRequiresSave' | translate)"
            [attr.tabindex]="isEdit ? null : 0"
          >
            <button
              mat-stroked-button
              type="button"
              data-testid="tf-ausfuehren"
              [disabled]="runDisabled()"
              (click)="openRunDialog()"
            >
              <mat-icon aria-hidden="true">play_arrow</mat-icon>
              {{ 'testCases.run.title' | translate }}
            </button>
          </span>
        }
        <button mat-stroked-button type="button" data-testid="tc-cancel-btn" (click)="onCancel()">
          {{ 'testCases.form.cancel' | translate }}
        </button>
        @if (!isReadOnly() && !loadError()) {
          <button
            mat-flat-button
            color="primary"
            type="button"
            data-testid="tf-speichern"
            [disabled]="saveDisabled()"
            (click)="onSave()"
          >
            <mat-icon aria-hidden="true">check</mat-icon>
            {{ (isEdit ? 'testCases.form.save' : 'testCases.form.saveCreate') | translate }}
          </button>
        }
      </div>
    </div>
    @if (saveError()) {
      <p class="tc-page__error tc-page__error--save tc-save-error" role="alert" data-testid="tc-save-error">
        @if (saveStale()) {
          {{ 'testCases.form.saveStale' | translate }}
        } @else {
          {{ saveErrorText() ?? ('testCases.form.saveError' | translate) }}
        }
        @if (missingRequirementKey(); as key) {
          {{ 'testCases.form.links.requirementNotFound' | translate: { key } }}
        }
        @if (duplicateRequirementLink()) {
          {{ 'testCases.form.links.requirementDuplicate' | translate }}
        }
      </p>
    }
  </div>

  @if (isArchived()) {
    <app-projekt-archiviert-hinweis class="tc-archiviert-hinweis" />
  } @else if (isProjectReader()) {
    <div class="tc-readonly-hint">
      <mat-icon class="tc-readonly-hint__icon" aria-hidden="true">info</mat-icon>
      <p class="tc-readonly-hint__text">{{ 'testCases.form.readOnlyHint' | translate }}</p>
    </div>
  }

  @if (loading()) {
    <div class="tc-page__loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="tc-page__error" role="alert">{{ 'testCases.form.loadError' | translate }}</p>
  } @else {
    <form class="tc-body" [formGroup]="form" (ngSubmit)="onSave()" (click)="verhindereUngewolltesAbsenden($event)">
      <div class="tc-main">
        @if (linkedRequirementId()) {
          <app-requirement-link-block
            [projectId]="projectId"
            [requirementId]="linkedRequirementId()"
          ></app-requirement-link-block>
        }

        <div class="tc-card">
          <div class="tc-card__body">
            <to-section-title
              [title]="basicsSectionKeys.title | translate"
              [subtitle]="basicsSectionKeys.subtitle | translate"
            >
              @if (isEdit) {
                <span right data-testid="basics-right-slot">
                  @if (linkedStoryKey() && !isArchived()) {
                    <button
                      type="button"
                      class="tc-source-refresh"
                      data-testid="source-refresh"
                      [disabled]="refreshing()"
                      (click)="refreshFromRequirement()"
                    >
                      <mat-icon aria-hidden="true">sync</mat-icon>
                      {{ 'testCases.form.source.refresh' | translate: { key: linkedStoryKey() } }}
                    </button>
                  }
                </span>
              }
            </to-section-title>

            @if (refreshError()) {
              <p class="tc-page__error" role="alert">{{ 'testCases.form.source.refreshError' | translate }}</p>
            }

            <div class="tc-basics-grid">
              <div class="tc-basics-grid__full">
                <mat-form-field appearance="outline" floatLabel="always" class="tc-grund-feld">
                  <mat-label>{{ 'testCases.form.title' | translate }}</mat-label>
                  <input
                    #titelFeld
                    matInput
                    formControlName="title"
                    data-testid="tf-titel"
                    required
                    [attr.maxlength]="fieldLengths.NAME_MAX"
                  />
                  <mat-hint align="end" aria-live="polite">
                    {{ form.controls.title.value.length }} / {{ fieldLengths.NAME_MAX }}
                  </mat-hint>
                  @if (form.controls.title.errors?.['required']) {
                    <mat-error>{{ 'testCases.form.titleRequired' | translate }}</mat-error>
                  } @else if (form.controls.title.errors?.['maxlength']) {
                    <mat-error>{{ 'testCases.form.titleTooLong' | translate }}</mat-error>
                  } @else if (form.controls.title.errors?.['server']) {
                    <mat-error>{{ form.controls.title.errors?.['server'] }}</mat-error>
                  }
                </mat-form-field>
              </div>

              <div class="tc-basics-grid__full">
                <mat-form-field
                  appearance="outline"
                  floatLabel="always"
                  class="tc-grund-feld tc-grund-feld--mehrzeilig"
                >
                  <mat-label>{{ 'testCases.form.description' | translate }}</mat-label>
                  <textarea
                    matInput
                    formControlName="description"
                    data-testid="tf-beschreibung"
                    rows="3"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                  ></textarea>
                </mat-form-field>
              </div>

              <div class="tc-basics-grid__full">
                <mat-form-field appearance="outline" floatLabel="always" class="tc-grund-feld">
                  <mat-label>{{ 'testCases.form.testGoal' | translate }}</mat-label>
                  <textarea
                    matInput
                    formControlName="testGoal"
                    data-testid="tf-kontext"
                    rows="1"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    [placeholder]="'testCases.form.testGoalPlaceholder' | translate"
                  ></textarea>
                </mat-form-field>
              </div>

              <div class="tc-basics-grid__full">
                <mat-form-field
                  appearance="outline"
                  floatLabel="always"
                  class="tc-grund-feld tc-grund-feld--mehrzeilig"
                >
                  <mat-label>{{ 'testCases.form.preconditions' | translate }}</mat-label>
                  <textarea
                    matInput
                    formControlName="preconditions"
                    data-testid="tf-voraussetzungen"
                    rows="4"
                    [attr.maxlength]="fieldLengths.FREETEXT_MAX"
                    [placeholder]="'testCases.form.preconditionsPlaceholder' | translate"
                  ></textarea>
                  @if (kiVorbedingungBefund() || kiVorbedingungGeaendert()) {
                    <mat-hint class="tc-ki-marker" data-testid="ki-vorbedingung-marker">
                      @if (kiVorbedingungBefund()) {
                        <app-ki-marker art="befund" />
                      }
                      @if (kiVorbedingungGeaendert()) {
                        <app-ki-marker art="geaendert" />
                      }
                    </mat-hint>
                  }
                </mat-form-field>
              </div>

              <div class="tc-basics-grid__full">
                <mat-form-field appearance="outline" floatLabel="always" class="tc-field tc-field--uppercase-label">
                  <mat-label>{{ 'testCases.form.requirementLink.fieldLabel' | translate }}</mat-label>
                  <input
                    #anforderungFeld
                    matInput
                    autocomplete="off"
                    data-testid="tc-anforderung-feld"
                    data-control-name="requirementId"
                    aria-haspopup="dialog"
                    [readonly]="isReadOnly()"
                    [value]="anforderungAnzeigeText()"
                    [placeholder]="'testCases.form.requirementLink.fieldPlaceholder' | translate"
                    (click)="oeffneAnforderungAuswahl()"
                    (keydown)="onAnforderungTaste($event)"
                    (input)="onAnforderungEingabe($event)"
                  />
                  @if (linkedRequirementId() && !isReadOnly()) {
                    <button
                      matSuffix
                      mat-icon-button
                      type="button"
                      data-testid="tc-anforderung-entfernen"
                      [attr.aria-label]="'testCases.form.requirementLink.remove' | translate"
                      (click)="entferneAnforderung()"
                    >
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  }
                </mat-form-field>
              </div>
            </div>
          </div>
        </div>

        @if (stepsReady()) {
          <app-test-step-table
            #stepTable
            [projectId]="projectId"
            [testCaseId]="testCaseId"
            [allowModuleEmbed]="true"
            [bufferedMode]="true"
            [initialSteps]="draftSteps()"
            [readOnly]="isReadOnly()"
            [canEditModule]="canEditModule()"
            conditionMode="list"
            [availableSheets]="availableSheets()"
            [showStepKeys]="true"
            [kiBefunde]="kiBefundeAmSchritt()"
            [testCaseAssigneeName]="testCaseAssigneeName()"
            (stepsChanged)="onStepsChanged($event)"
            (moduleOpen)="onOpenModule($event)"
            (editModule)="onEditModule($event)"
            (kiUebernehmen)="kiQualitaetsCheck.uebernehmen($event)"
            (kiLassen)="kiQualitaetsCheck.lassen($event)"
          >
            <app-ki-qualitaets-check
              #kiQualitaetsCheck
              stepsCheck
              [projectId]="projectId"
              [testCaseId]="testCaseId"
              [titel]="kiTitel()"
              [vorbedingung]="kiVorbedingung()"
              [schritte]="draftSteps()"
              [anfrageId]="kiAnfrageId()"
              [readOnly]="isReadOnly()"
              (verbessern)="onKiVerbessern($event)"
            />
          </app-test-step-table>
        }

        <app-module-edit-drawer
          [projectId]="projectId"
          [moduleId]="editingModuleId()"
          [availableSheets]="availableSheets()"
          [readOnly]="isReadOnly()"
          (closed)="onModuleDrawerClosed()"
          (saved)="onModuleSaved()"
          (sheetUnlinked)="onModuleSheetUnlinked($event)"
          (sheetsChanged)="availableSheets.set($event)"
        ></app-module-edit-drawer>

        <app-test-case-details-historie
          [projectId]="projectId"
          [testCaseId]="testCaseId"
          [current]="testCase()"
          [readonly]="isReadOnly()"
          [personOptions]="personOptions()"
          [currentUserId]="currentUserId()"
          [notesFilled]="notizGesetzt()"
          (chainChanged)="onHandoverChainChanged()"
        >
          <div class="tc-notizen">
            <label class="tc-notizen__label" for="tf-notizen-feld">
              {{ 'testCases.form.notes.label' | translate }}
            </label>
            <textarea
              id="tf-notizen-feld"
              class="tc-notizen__feld"
              formControlName="notes"
              rows="3"
              data-testid="tf-notizen"
              [attr.maxlength]="fieldLengths.FREETEXT_MAX"
              [placeholder]="'testCases.form.notes.placeholder' | translate"
            ></textarea>
            <div class="tc-notizen__zaehler" aria-live="polite">
              {{ form.controls.notes.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
            </div>
          </div>
        </app-test-case-details-historie>
      </div>

      <div class="tc-sidebar">
        <app-test-case-eigenschaften
          [projectId]="projectId"
          [readOnly]="isReadOnly()"
          [personOptions]="personOptions()"
          [version]="testCase()?.version ?? null"
          (listenGeladen)="onAuswahllistenGeladen($event)"
        />

        <app-test-case-automation-card
          #automationCard
          [frameworkControl]="form.controls.automationFramework"
          [locatorControl]="form.controls.automationLocator"
          [automationKey]="testCase()?.automationKey ?? null"
          [projectId]="projectId"
          [testCaseId]="testCaseId"
          [testCaseTitle]="form.controls.title.value ?? ''"
          [canWrite]="isEdit && !isReadOnly()"
        ></app-test-case-automation-card>

        <app-test-case-usage [projectId]="projectId" [testCaseId]="testCaseId" />

        <app-test-case-links
          #linksBlock
          [projectId]="projectId"
          [testCaseId]="testCaseId"
          [readOnly]="isReadOnly()"
          (openModalRequested)="showLinkModal.set(true)"
          (draftLinksChange)="onDraftLinksChanged($event)"
          (pendingDeletionsChange)="onPendingDeletionsChanged($event)"
        ></app-test-case-links>

        <app-test-case-execution-stats
          [projectId]="projectId"
          [testCaseId]="testCaseId"
        ></app-test-case-execution-stats>

        @if (isEdit && testCaseId; as caseId) {
          <app-project-documents-card
            [projectId]="projectId"
            targetType="test-cases"
            [targetId]="caseId"
            [canWrite]="!isReadOnly()"
          />
        }
      </div>
    </form>

    @if (anforderungAuswahlOffen()) {
      <app-anforderung-auswahl-dialog
        [projectId]="projectId"
        [value]="anforderungAnzeige()?.key ?? null"
        [initialQuery]="anforderungSuchtext()"
        (picked)="onAnforderungGewaehlt($event)"
        (closed)="schliesseAnforderungAuswahl()"
      />
    }

    <app-add-link-modal
      [projectId]="projectId"
      [excludedRequirementKeys]="linksBlock.requirementKeys()"
      [visible]="showLinkModal()"
      (visibleChange)="showLinkModal.set($event)"
      (added)="linksBlock.onAdd($event)"
    />
  }
</div>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'common.unsavedChanges.title' | translate"
  [text]="deactivateMessage() | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="deactivateConfirmLabel() | translate"
  [cancelText]="deactivateCancelLabel() | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
